import type { AddSettlement, DueCandidateDto, DueInstalmentDto, DueItemDto, DuesDto, DueState } from '@bk/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { dateOnly } from '../../lib/format';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, PageTitle, SectionLoader, Select } from '../../ui';

export const euro = (n: number) => `${n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const SECTIONS: Array<{ stato: Exclude<DueState, 'pagata'>; titolo: string; tone: 'red' | 'amber' | 'blue' | 'gray' }> = [
  { stato: 'scaduta', titolo: 'Scaduto', tone: 'red' },
  { stato: 'in_scadenza', titolo: 'In scadenza', tone: 'amber' },
  { stato: 'aperta', titolo: 'Più avanti', tone: 'blue' },
  { stato: 'senza_scadenza', titolo: 'Senza scadenza scritta', tone: 'gray' },
];

/** Una riga dell'elenco: una rata ancora da saldare della sua fattura. */
type Row = { item: DueItemDto; rata: DueInstalmentDto; n: number };

/**
 * Lo scadenzario delle fatture (ADR 0254): che cosa resta da pagare ai fornitori e da incassare dai clienti, rata per
 * rata, con la scadenza scritta nel file della fattura. Il residuo toglie i pagamenti certi o confermati; le proposte si
 * vedono e contano solo quando la persona le conferma. Ognuno vede le fatture dei suoi permessi (ADR 0232).
 */
export function ScadenzarioPage() {
  const { livello } = useTenant();
  const [sede, setSede] = useState<string>(livello ?? '');
  const [verso, setVerso] = useState<DueItemDto['verso']>('da_pagare');
  const q = useTenantQuery<DuesDto>(['scadenze', sede], `/scadenze${sede ? `?sede=${sede}` : ''}`);
  const d = q.data;
  const rows: Row[] = (d?.voci ?? [])
    .filter((v) => v.verso === verso)
    .flatMap((item) => item.rate.map((rata, i) => ({ item, rata, n: i + 1 })).filter((r) => r.rata.residuo > 0));
  const totals = d?.totali[verso];
  return (
    <div>
      <PageTitle
        title="Scadenzario"
        subtitle="Che cosa resta da pagare ai fornitori e da incassare dai clienti, con la scadenza scritta nella fattura."
        actions={
          d && (d.sedi.length > 0 || sede) ? (
            <div className="w-56">
              <Select aria-label="Sede" value={sede} onChange={(e) => setSede(e.target.value)}>
                <option value="">Tutto</option>
                <option value="azienda">Solo l’azienda</option>
                {d.sedi.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                  </option>
                ))}
              </Select>
            </div>
          ) : undefined
        }
      />
      <nav className="mb-4 flex flex-wrap gap-2">
        <Button variant={verso === 'da_pagare' ? 'primary' : 'ghost'} onClick={() => setVerso('da_pagare')}>
          Da pagare
        </Button>
        <Button variant={verso === 'da_incassare' ? 'primary' : 'ghost'} onClick={() => setVerso('da_incassare')}>
          Da incassare
        </Button>
      </nav>
      <ErrorText>{q.error ? errorMessage(q.error) : ''}</ErrorText>
      {q.isLoading && <SectionLoader />}
      {d && totals && (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Total label="Scaduto" value={totals.scaduto} tone="text-red-700" />
            <Total label={`Entro ${d.giorni} giorni`} value={totals.in_scadenza} tone="text-amber-700" />
            <Total label="Più avanti" value={totals.aperto} />
            <Total label="Senza scadenza scritta" value={totals.senza_scadenza} />
          </div>
          {!rows.length && <Empty>{verso === 'da_pagare' ? 'Nessuna fattura da pagare.' : 'Nessuna fattura da incassare.'}</Empty>}
          <div className="space-y-4">
            {SECTIONS.map((s) => {
              const list = rows.filter((r) => r.rata.stato === s.stato);
              return list.length ? (
                <Card key={s.stato} className="!p-4">
                  <h2 className="flex items-center gap-2 text-sm font-semibold">
                    {s.titolo} <Badge tone={s.tone}>{list.length}</Badge>
                  </h2>
                  <ul className="mt-2 divide-y divide-slate-100">
                    {list.map((r) => (
                      <DueRow key={`${r.item.tipo}/${r.item.rif}/${r.n}`} row={r} />
                    ))}
                  </ul>
                </Card>
              ) : null;
            })}
          </div>
          <Excluded d={d} />
        </>
      )}
    </div>
  );
}

function Total({ label, value, tone = '' }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-xl bg-white p-3 shadow-sm">
      <p className="text-xs text-ink/60">{label}</p>
      <p className={`mt-1 text-lg font-semibold tabular-nums ${tone}`}>{euro(value)}</p>
    </div>
  );
}

function when(r: DueInstalmentDto): string {
  if (!r.scadenza) return 'senza scadenza scritta';
  if (r.giorni === null) return dateOnly(r.scadenza);
  if (r.giorni < 0) return `scaduta il ${dateOnly(r.scadenza)} (${-r.giorni} ${r.giorni === -1 ? 'giorno' : 'giorni'} fa)`;
  if (r.giorni === 0) return 'scade oggi';
  return `scade il ${dateOnly(r.scadenza)} (tra ${r.giorni} ${r.giorni === 1 ? 'giorno' : 'giorni'})`;
}

function DueRow({ row }: { row: Row }) {
  const { tenant, isManager } = useTenant();
  const [open, setOpen] = useState(false);
  const { item, rata, n } = row;
  const proposals = item.coperture.filter((c) => c.stato === 'proposto');
  const advances = item.coperture.filter((c) => c.acconto && c.stato !== 'proposto');
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        {isManager ? (
          <a className="font-medium text-accent hover:underline" href={`#/t/${tenant.id}/e/${item.tipo}/${encodeURIComponent(item.rif)}`}>
            {item.nome}
          </a>
        ) : (
          <span className="font-medium">{item.nome}</span>
        )}
        {item.controparte && <span className="text-ink/70">{item.controparte}</span>}
        {item.data && <span className="text-xs text-ink/50">del {dateOnly(item.data)}</span>}
        <span className="ml-auto font-semibold tabular-nums">{euro(rata.residuo)}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink/60">
        <span>{when(rata)}</span>
        {item.rate.length > 1 && (
          <span>
            · rata {n} di {item.rate.length} ({euro(rata.importo)})
          </span>
        )}
        <span>· fattura di {euro(item.dovuto)}</span>
        {item.pagato > 0 && <Badge tone="green">{advances.length ? `acconto ${euro(advances.reduce((x, c) => x + c.importo, 0))}` : `pagati ${euro(item.pagato)}`}</Badge>}
        {proposals.length > 0 && <Badge tone="amber">{euro(item.proposto)} da confermare</Badge>}
      </div>
      {isManager && (
        <div className="mt-1">
          <Button variant="ghost" className="!px-2 !py-1 text-xs" onClick={() => setOpen((x) => !x)}>
            {open ? 'Chiudi' : item.verso === 'da_pagare' ? 'Collega un pagamento' : 'Collega un incasso'}
          </Button>
          {open && <ManualLink item={item} onDone={() => setOpen(false)} />}
        </div>
      )}
    </li>
  );
}

function Excluded({ d }: { d: DuesDto }) {
  const e = d.esclusi;
  const parts = [
    e.pagate ? `${e.pagate} ${e.pagate === 1 ? 'fattura pagata' : 'fatture pagate'}` : null,
    e.note_di_credito ? `${e.note_di_credito} ${e.note_di_credito === 1 ? 'nota di credito' : 'note di credito'}` : null,
    e.senza_importo ? `${e.senza_importo} senza importo` : null,
    e.senza_verso ? `${e.senza_verso} senza la direzione (emessa o ricevuta)` : null,
  ].filter(Boolean);
  return parts.length ? <p className="mt-4 text-xs text-ink/55">Non in elenco: {parts.join(', ')}.</p> : null;
}

/**
 * Il collegamento a mano di un pagamento o di un incasso (ADR 0245): i movimenti del verso giusto con una parte ancora
 * libera, dal più vicino per importo e per data; l'importo proposto è il minore tra il libero e il residuo.
 */
export function ManualLink({ item, onDone }: { item: Pick<DueItemDto, 'tipo' | 'rif' | 'verso' | 'residuo' | 'data'>; onDone?: () => void }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const params = new URLSearchParams({ tipo: item.tipo, verso: item.verso, residuo: String(item.residuo), ...(item.data ? { data: item.data } : {}), ...(text.trim() ? { testo: text.trim() } : {}) });
  const q = useTenantQuery<{ data: DueCandidateDto[] }>(['scadenze', 'movimenti', item.tipo, item.rif, text.trim()], `/scadenze/movimenti?${params.toString()}`);
  const [chosen, setChosen] = useState<DueCandidateDto | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pick = (m: DueCandidateDto) => {
    setChosen(m);
    setAmount(Math.min(m.libero, item.residuo).toFixed(2).replace('.', ','));
  };
  const save = async () => {
    if (!chosen) return;
    const value = Number(amount.replace(/\./g, '').replace(',', '.'));
    if (!(value > 0)) return setError('Scrivi un importo maggiore di zero');
    setBusy(true);
    setError(null);
    try {
      await api(t('/scadenze/collegamenti'), { body: { movimento: { tipo: chosen.tipo, rif: chosen.rif }, cosa: { tipo: item.tipo, rif: item.rif }, importo: value } satisfies AddSettlement });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'scadenze'] }),
        queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id, 'model', 'settlements'] }),
      ]);
      onDone?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const list = q.data?.data ?? [];
  return (
    <div className="mt-2 rounded-lg border border-black/[0.06] bg-paper p-3">
      <Field label={item.verso === 'da_pagare' ? 'Cerca tra le uscite del conto' : 'Cerca tra le entrate del conto'} placeholder="Parole della descrizione" value={text} onChange={(e) => setText(e.target.value)} />
      {q.isLoading && <SectionLoader />}
      {q.isSuccess && !list.length && <p className="mt-2 text-xs text-ink/60">Nessun movimento con una parte ancora da collegare.</p>}
      <ul className="mt-2 max-h-64 divide-y divide-slate-100 overflow-y-auto">
        {list.map((m) => (
          <li key={`${m.tipo}/${m.rif}`}>
            <button type="button" className={`flex w-full flex-wrap items-baseline gap-x-2 px-1 py-2 text-left text-sm hover:bg-black/[0.03] ${chosen?.rif === m.rif ? 'bg-accent/10' : ''}`} onClick={() => pick(m)}>
              <span className="text-xs text-ink/55">{dateOnly(m.data)}</span>
              <span className="min-w-0 flex-1 truncate">{m.descrizione ?? m.nome ?? m.rif}</span>
              <span className="tabular-nums">{euro(m.importo)}</span>
              {m.libero !== Math.abs(m.importo) && <span className="text-xs text-ink/55">liberi {euro(m.libero)}</span>}
            </button>
          </li>
        ))}
      </ul>
      {chosen && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <div className="w-40">
            <Field label="Importo che copre" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <Button disabled={busy} onClick={() => void save()}>
            {busy ? 'Collego…' : 'Collega'}
          </Button>
        </div>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}
